import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import CheckoutSection from './utils/Checkout.Section';
import CheckoutSectionContent from './utils/Checkout.SectionContent';
import CheckoutSectionFooter from './utils/Checkout.SectionFooter';
import {
  acceptTermsOfCondition as setAcceptTermsOfCondition,
  setStatusSubmitButton,
  setValidationErrors,
  submit,
  submitDone,
  setCheckoutIsSubmitting,
  saveCustomerDetail,
} from '../../Actions/Checkout.action';
import {
  alwaysRequiredSchema,
  businessCustomerDetailsSchema,
  dropshipValidationSchema,
  privateCustomerAddressSchema,
  privateCustomerAlternativeAddressSchema,
} from './utils/Checkout.ValidationSchema';
import { translate } from '../../Services/translation';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import Checkbox from '../Inputs/Checkbox';
import clsx from 'clsx';
import { ValidationError } from 'yup';
import { triggerToast } from '../../Services/Toast.service';

const ConfirmOrder = () => {
  const dispatch = useDispatch();
  const checkout = useTypedSelector((state) => state.checkout);
  const {
    payload,
    validationErrors = {},
    checkoutIsSubmitting,
  } = checkout;
  const {
    customerDetails,
    alternativeAddress,
    selectedDeliveryAddressId,
    selectedBillingAddressId,
    selectedDeliveryMethod,
    selectedPaymentMethod,
    showAlternativeAddress,
    isBusinessCustomer,
    acceptTermsOfCondition,
    useDropshipping,
    selectedDropshipAddressType,
    dropshipCustomerDetails,
  } = payload;

  const onSubmit = useCallback(() => dispatch(submit()), [dispatch]);

  const placeOrder = useCallback(async () => {
    dispatch(setCheckoutIsSubmitting(true));
    const notCustomerDetailFields = [
      'selectedDeliveryAddressId',
      'selectedBillingAddressId',
      'selectedPaymentMethod',
      'selectedDeliveryMethod',
      'acceptTermsOfCondition',
    ];

    const onValidationFailed = (stateKey: string, errors: any[]) => {
      const key = (err: any) =>
        stateKey && !notCustomerDetailFields.includes(err.path)
          ? `${stateKey}-${err.path}`
          : err.path;

      const newValidationErrors = errors.reduce(
        (prev, err) => ({
          ...prev,
          [key(err)]: err.errors,
        }),
        {}
      );
      dispatch(
        setValidationErrors({
          ...validationErrors,
          ...newValidationErrors,
        })
      );
      dispatch(setStatusSubmitButton(true));
      dispatch(submitDone(null));
    };

    dispatch(setStatusSubmitButton(false));

    // Validate, then submit. Not proud of this validation, but better than Litium default
    try {
      if (isBusinessCustomer) {
        // Business customer
        try {
          const schema = useDropshipping
            ? dropshipValidationSchema
            : businessCustomerDetailsSchema;

          await schema.validate(
            {
              ...(useDropshipping
                ? dropshipCustomerDetails
                : customerDetails),
              selectedDeliveryAddressId,
              selectedBillingAddressId,
              useDropshipping,
              selectedDropshipAddressType,
            },
            { abortEarly: false }
          );
        } catch (ex) {
          // re-throw the error with the scheme's state key
          throw {
            stateKey: useDropshipping
              ? 'dropshipCustomerDetails'
              : 'customerDetails',
            error: ex.inner,
          };
        }
      } else {
        // Private customer
        try {
          await privateCustomerAddressSchema.validate(
            {
              ...customerDetails,
              selectedDropshipAddressType,
              useDropshipping,
            },
            { abortEarly: false }
          );
        } catch (ex) {
          // re-throw the error with the scheme's state key
          throw {
            stateKey: 'customerDetails',
            error: ex.inner,
          };
        }

        // Alternative address
        if (showAlternativeAddress) {
          try {
            await privateCustomerAlternativeAddressSchema.validate(
              {
                ...alternativeAddress,
              },
              { abortEarly: false }
            );
          } catch (ex) {
            // re-throw the error with the scheme's state key
            throw {
              stateKey: 'alternativeAddress',
              error: ex.inner,
            };
          }
        }
      }

      // Always required
      try {
        await alwaysRequiredSchema.validate(
          {
            acceptTermsOfCondition,
            selectedPaymentMethod,
            selectedDeliveryMethod,
          },
          { abortEarly: false }
        );
      } catch (ex) {
        // re-throw the error with the scheme's state key
        throw { stateKey: null, error: ex.inner };
      }

      // Will not be reached if any validation failed
      dispatch(saveCustomerDetail()).then(() => onSubmit());
    } catch (err) {
      dispatch(setCheckoutIsSubmitting(false));
      onValidationFailed(err.stateKey, err.error);

      // Trigger toasts for validation errors if B2B and not using drop shipping
      if (err instanceof ValidationError) {
        let validationErr = err as ValidationError;
        if (isBusinessCustomer) {
          triggerToast({
            title: translate('general.error'),
            text: 'Validation error, contact customer support',
            type: 'error',
            autoClose: false
          });

          for (let error of validationErr.inner) {
            console.error({
              title: translate(`checkout.error.${error.path}`),
              text: error.message,
              type: 'error',
            });
          }
        }
      }
    }
  }, [
    dispatch,
    validationErrors,
    isBusinessCustomer,
    customerDetails,
    selectedDeliveryAddressId,
    selectedBillingAddressId,
    showAlternativeAddress,
    alternativeAddress,
    acceptTermsOfCondition,
    selectedPaymentMethod,
    selectedDeliveryMethod,
    onSubmit,
    useDropshipping,
  ]);

  const acceptTermsError = useMemo(
    () =>
      validationErrors['acceptTermsOfCondition']
        ? validationErrors['acceptTermsOfCondition'][0]
        : null,
    [validationErrors]
  );

  return (
    <div>
      <div>
        <p className="font-bold">{translate('checkout.confirm-order')}</p>
        <p className="mb-3 text-lg">
          {translate('checkout.review-order')}
        </p>
        <Checkbox
          inputSize="large"
          id="terms-acceptterms"
          checked={acceptTermsOfCondition}
          onChange={(e) =>
            dispatch(setAcceptTermsOfCondition(e.target.checked))
          }
          inputState={acceptTermsError ? 'error' : 'default'}
          label={
            <>
              {translate(`checkout.terms.acceptterms`)}{' '}
              <a href={payload.termsUrl} className="link underline">
                {translate(`checkout.terms.link`)}
              </a>
            </>
          }
          message={acceptTermsError}
        />
      </div>
      <div className={clsx('flex', 'mt-2')}>
        <button
          className={clsx(
            'btn',
            'btn--black',
            'btn--xlarge',
            'flex-1',
            'flex',
            'items-center',
            'justify-center'
          )}
          disabled={checkoutIsSubmitting}
          onClick={() => placeOrder()}
          type="submit"
        >
          {!checkoutIsSubmitting && translate('checkout.placeorder')}
          {
            checkoutIsSubmitting && (
              <>{translate('general.loading')}...</>
            ) /* TODO Loading spinner or similar */
          }
        </button>
      </div>
    </div>
  );
};

export default ConfirmOrder;
